import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckBox from '@mui/icons-material/CheckBox'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { IconButton } from '@mui/material'
import { TenantSettingsContext } from '../../../../App'
import { getTravelPay } from '../../../../helpers/shiftTravel'

const Shifts = ({ employeeID }) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [loading, setLoading] = useState(true)
  const [shifts, setShifts] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [noMore, setNoMore] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const navigate = useNavigate()

  const getShifts = () => {
    setLoading(true)
    API.get(
      `/shifts-by-employee?skip=${shifts.length}&employeeID=${employeeID}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setShifts((prev) => [...prev, ...res.data])
          // TODO
          if (res.data.length < 10) {
            setNoMore(true)
          }
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => getShifts(), [])

  // No Paper wrapper: this now renders inside the Payroll & Shifts tab, which
  // already sits in the tabs' Paper
  return (
    <Box>
      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title2">Booked Shifts</Typography>
          <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            open={!!menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
            anchorEl={menuAnchorEl}
          >
            <MenuItem
              onClick={() =>
                navigate('/shift-application-history/' + employeeID)
              }
            >
              Shift Application History
            </MenuItem>
          </Menu>
        </Stack>
        {loading && <LinearProgress />}
        {fetchError && (
          <Alert severity="error">
            Unable to fetch Shifts. Please try again later.
          </Alert>
        )}
        <CustomDataGrid
          rows={shifts}
          onRowClick={({ row }) =>
            row?.event?._id && window.open(`/events/${row.event._id}`, '_blank')
          }
          slots={{
            noRowsOverlay: () => (
              <Alert severity="warning">No Shifts Found</Alert>
            ),
            noResultsOverlay: () => (
              <Alert sx={{ zIndex: 10 }} severity="warning">
                No Shifts Found
              </Alert>
            ),
            loadingOverlay: LinearProgress,
            footer: () => (
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {!noMore && (
                  <Button
                    variant="contained"
                    color="anMainBlue"
                    size="small"
                    onClick={() => getShifts()}
                  >
                    Load More...
                  </Button>
                )}
              </Box>
            ),
          }}
          columns={[
            {
              field: 'start',
              minWidth: 150,
              valueGetter: (value, row) => moment(row.start).unix().toString(),
              headerName: 'Start',
              renderCell: ({ row }) =>
                moment(row.start).format('DD/MM/YYYY').toString(),
            },
            {
              field: 'end',
              minWidth: 150,
              valueGetter: (value, row) => moment(row.end).unix().toString(),
              headerName: 'End',
              renderCell: ({ row }) =>
                moment(row.end).format('DD/MM/YYYY').toString(),
            },
            {
              field: 'title',
              minWidth: 300,
              flex: 1,
              valueGetter: (value, row) =>
                row?.shiftType === 'PTS'
                  ? 'Patient Transport'
                  : (row?.event?.title ?? ''),
              headerName: 'Title',
            },
            {
              field: 'rate',
              minWidth: 150,
              valueGetter: (value, row) => row.payRate,
              renderCell: ({ row }) => {
                if (!row.payRate) return null
                const bonusTotal = (row.bonuses ?? []).reduce(
                  (sum, bonus) => sum + (Number(bonus?.amount) || 0),
                  0,
                )
                const travelTotal = getTravelPay(row.travel)
                return `${orgSettings?.currency ?? '£'}${row.payRate}/${row.hourlyRate ? 'hour' : 'shift'}${bonusTotal ? ` + ${orgSettings?.currency ?? '£'}${bonusTotal} bonus` : ''}${travelTotal ? ` + ${orgSettings?.currency ?? '£'}${travelTotal} travel` : ''}`
              },
              headerName: 'Pay Rate',
            },
            {
              field: 'isVerified',
              headerAlign: 'center',
              align: 'center',
              minWidth: 100,
              renderCell: ({ row }) =>
                row.isVerified && <CheckBox color="success" />,
              headerName: 'Verified',
            },
            {
              field: 'isPaid',
              minWidth: 100,
              headerAlign: 'center',
              align: 'center',
              renderCell: ({ row }) =>
                row.isPaid && <CheckBox color="success" />,
              headerName: 'Paid',
            },
          ]}
        />
      </Stack>
    </Box>
  )
}

export default Shifts
